import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminDataRoomsQuery } from '../../../features/admin/hooks/use-admin-api'

type SearchParams = { page?: number; search?: string }
const LIMIT = 20

export const Route = createFileRoute('/admin/data-rooms/')({
  component: Page,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type DataRoomItem = {
  id: string
  token: string
  startupId: string
  startupName: string
  createdByUserId: string | null
  createdByEmail: string | null
  sessionsCount: number
  revokedAt: string | Date | null
  createdAt: string | Date
}

const columns: TableColumn<DataRoomItem>[] = [
  {
    header: 'ID',
    width: '80px',
    render: (room) => (
      <Link
        to="/admin/data-rooms/$id"
        params={{ id: room.id }}
        className="font-mono text-xs text-primary hover:underline"
      >
        {room.id.slice(0, 8)}
      </Link>
    ),
  },
  {
    header: 'Startup',
    render: (room) => (
      <Link
        to="/admin/startups/$id"
        params={{ id: room.startupId }}
        className="text-sm font-medium text-primary hover:underline"
      >
        {room.startupName}
      </Link>
    ),
  },
  {
    header: 'Token',
    width: '140px',
    render: (room) => <CellText className="font-mono text-xs">{room.token}</CellText>,
  },
  {
    header: 'Sessions',
    width: '80px',
    render: (room) => <span className="font-medium tabular-nums">{room.sessionsCount}</span>,
  },
  {
    header: 'Status',
    width: '100px',
    render: (room) => (
      <Badge variant={room.revokedAt ? 'destructive' : 'success'} size="sm">
        {room.revokedAt ? 'Revoked' : 'Active'}
      </Badge>
    ),
  },
  {
    header: 'Created By',
    render: (room) =>
      room.createdByUserId ? (
        <Link
          to="/admin/users/$id"
          params={{ id: room.createdByUserId }}
          className="text-sm text-primary hover:underline"
        >
          {room.createdByEmail}
        </Link>
      ) : (
        <CellText>{room.createdByEmail ?? '\u2014'}</CellText>
      ),
  },
  {
    header: 'Created',
    width: '200px',
    render: (room) => <CellText>{formatDate(room.createdAt)}</CellText>,
  },
]

function Page() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const dataRoomsQuery = useAdminDataRoomsQuery({ page, limit: LIMIT, search: search || undefined })

  const dataRooms = dataRoomsQuery.data?.data?.items ?? []
  const total = dataRoomsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-4">
      <SearchInput
        placeholder="Search by startup name..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={dataRooms}
        isLoading={dataRoomsQuery.isLoading}
        emptyMessage="No data rooms found."
        keyExtractor={(r) => r.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
